
import { useEffect, useState } from "react";
import { RouterProvider } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./components/ToastTheme.css";
import SessionLoading from "./pages/common/SessionLoading";

import router from "./router";
import Loader from "./pages/common/Loader";
import { useLoaderStore } from "./store/loaderStore";
import { useThemeStore } from "./store/themeStore";
import useDevice from "./hooks/useDevice";
import { authStore, useAuthStore } from "./store/authStore";
import { getCompanyThemes } from "./utils/companyTheme";
import { getCookie } from "./utils/api";
import { PullRefreshProvider } from "./layouts/PullRefreshContext";

function App() {
  const { loading } = useLoaderStore();
  const { theme } = useThemeStore();
  const isMobile = useDevice();
  const [sessionRestored, setSessionRestored] = useState(false);
  const { user } = useAuthStore();
  const companyId = user?.company_id;

  useEffect(() => {
    let active = true;
    let latestRequest = 0;
    useThemeStore.getState().setCompanyThemes([]);
    if (!companyId) return;

    const loadCompanyTheme = async () => {
      const request = ++latestRequest;
      const revision = useThemeStore.getState().companyThemeRevision;
      try {
        const themes = await getCompanyThemes(companyId);
        if (active && request === latestRequest &&
            revision === useThemeStore.getState().companyThemeRevision) {
          useThemeStore.getState().setCompanyThemes(themes);
        }
      } catch (error) {
        if (active) console.error("Unable to load company theme:", error.message);
      }
    };

    loadCompanyTheme();
    window.addEventListener("focus", loadCompanyTheme);
    return () => {
      active = false;
      window.removeEventListener("focus", loadCompanyTheme);
    };
  }, [companyId]);

  // =========================================
  // RESTORE SESSION
  // =========================================
  useEffect(() => {
    const restoreSession = async () => {
      console.log("🔄 Restoring session...");

      const token = getCookie("auth_token");
      const userJson = getCookie("auth_user");

      console.log("📦 Token:", token);
      console.log("📦 User JSON:", userJson);

      if (token && userJson) {
        try {
          const user = JSON.parse(userJson);

          console.log("✅ Parsed user:", user);

          if (user && user.user_id) {
            authStore.setuserdata(user, token);

            console.log(
              "✅ Store updated with user:",
              authStore.getAuthData().user
            );
          } else {
            console.warn("⚠️ Invalid user object");
            authStore.logout();
          }
        } catch (error) {
          console.error("❌ Session restore failed:", error);
          authStore.logout();
        }
      } else {
        console.log("ℹ️ No session cookies found.");
      }

      await new Promise((resolve) => setTimeout(resolve, 500));

      setSessionRestored(true);

      console.log("✅ Session restore complete.");
    };

    restoreSession();
  }, []);

  // =========================================
  // WAIT FOR SESSION RESTORE
  // =========================================
  if (!sessionRestored) {
    return <SessionLoading />;
  }

  // =========================================
  // APP
  // =========================================
  return (
    <>
      {loading && <Loader />}

      <PullRefreshProvider>
        <RouterProvider router={router} />
      </PullRefreshProvider>

      <ToastContainer
        className={`app-toast-container ${isMobile ? "app-toast-mobile" : "app-toast-desktop"}`}
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
        draggable
        theme={isMobile ? theme : "light"}
      />
    </>
  );
}

export default App;
